
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { WaitlistFormValues } from "@/components/waitlist/schema";

export function useWaitlistPreferences() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [waitlistActive, setWaitlistActive] = useState(true);
  const [savedPreferences, setSavedPreferences] = useState<WaitlistFormValues | null>(null);

  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('waitlist_active, saved_preferences')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      if (data) {
        setWaitlistActive(data.waitlist_active);
        // Fix type error by properly casting the JSON data to WaitlistFormValues
        if (data.saved_preferences) {
          setSavedPreferences(data.saved_preferences as WaitlistFormValues);
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateWaitlistStatus = async (active: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ waitlist_active: active })
        .eq('id', user?.id);

      if (error) throw error;

      setWaitlistActive(active);
      toast.success(`Waitlist ${active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error updating waitlist status:', error);
      toast.error('Failed to update waitlist status');
    }
  };

  const savePreferences = async (preferences: WaitlistFormValues) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ saved_preferences: preferences })
        .eq('id', user?.id);

      if (error) throw error;

      setSavedPreferences(preferences);
      console.log('Preferences saved successfully');
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  return {
    isLoading,
    waitlistActive,
    savedPreferences,
    updateWaitlistStatus,
    savePreferences
  };
}
